import { ArslActionCellUpdate, ArsListingState, HEADER_ROW_ID } from "../interface";

/**
 * Updates listing header text, display pattern, or annotation result pattern for a cell edit.
 *
 * @param state - Current ARS listing state
 * @param action - Cell-update action describing the edited cell
 * @returns Updated ARS listing state, or the original state when no edit applies
 */
export const arsl_cellUpdate = <T>(
  state: ArsListingState<T>,
  action: ArslActionCellUpdate<T>,
): ArsListingState<T> => {
  const targetColumn = state.bodyData.arsListingColumns.find((col) => col.id === action.colId);

  if (!targetColumn) {
    return state;
  }

  /* Update from a header cell */
  if (action.rowId === HEADER_ROW_ID) {
    if (state.settings.tlfViewMode.value === "ANNOTATIONS") {
      return state;
    }

    const updatedColumn = {
      ...targetColumn,
      headerText: action.value,
    };

    return {
      ...state,
      changed: true,
      bodyData: {
        ...state.bodyData,
        arsListingColumns: state.bodyData.arsListingColumns.map((col) =>
          col.id === action.colId ? updatedColumn : col,
        ),
      },
    };
  }

  /* Update from a body cell */
  const updatedColumn = { ...targetColumn };
  /* Update to annotations */
  if (state.settings.tlfViewMode.value === "ANNOTATIONS") {
    updatedColumn.resultPattern = state.settings.editorProps.getTextFromT(action.value).join("\n");
  } else {
    /* Update to disable pattern */
    const rowIndex = state.bodyData.arsBodyRows.findIndex((row) => row.id === action.rowId);
    /* Update a the only displayPattern in the column if it's the first row and the column has a single displayPattern */
    if (updatedColumn.displayPattern.length === 1) {
      if (rowIndex <= 0) {
        updatedColumn.displayPattern = [action.value];
      } else {
        /* Change to a displayPattern array */
        const displayPatternArray = new Array(state.bodyData.arsBodyRows.length).fill(
          updatedColumn.displayPattern[0],
        );
        displayPatternArray[rowIndex] = action.value;
        updatedColumn.displayPattern = displayPatternArray;
      }
    } else {
      /* Update existing displayPattern array */
      const displayPatternArray = [...updatedColumn.displayPattern];
      /* Extend the array if the rowIndex is greater than the current length */
      while (rowIndex >= displayPatternArray.length)
        displayPatternArray.push(...updatedColumn.displayPattern);
      displayPatternArray[rowIndex] = action.value;
      updatedColumn.displayPattern = displayPatternArray;
    }
  }

  /* Create new array */
  const updatedColumns = state.bodyData.arsListingColumns.map((col) =>
    col.id === action.colId ? updatedColumn : col,
  );

  return {
    ...state,
    changed: true,
    bodyData: {
      ...state.bodyData,
      arsListingColumns: updatedColumns,
    },
  };
};
